import { useMemo, useRef } from 'react';
import { Button, Col, ConfigProvider, Divider, Flex, Modal, Row, Steps, Typography } from 'antd';
import { useTheme } from 'styled-components';
import { BiTargetLock } from 'react-icons/bi';
import { LuArrowLeft, LuArrowRight, LuPlay } from 'react-icons/lu';

import { useObjectPickerStore, PICKER_STEPS } from '@stores/useObjectPickerStore';
import { AREA_MODE, COLLECTION_RESULT_LAYER_IDS } from '@utils/constants';

import { useAreaDraw } from '@hooks/useAreaDraw';

import Collections from './collections/Collections';
import AreaStep from './steps/AreaStep';
import RunStep from './steps/RunStep';
import { useRunDetection } from './useRunDetection';
import ObjectPickerTour from './ObjectPickerTour';

const STEP_META = [
	{ title: 'Collection', subtitle: 'Choose the collection of objects to find' },
	{ title: 'Area', subtitle: 'Choose where to run detection' },
	{ title: 'Run', subtitle: 'Confirm the run — nothing has started yet' },
];

const DRAW_DIALOGUE = {
	header: 'Draw area of interest',
	body: (
		<p>Sketch a polygon around the region you want to run detection on. Press ESC to cancel.</p>
	),
};

// Sketching an area needs a clear view of the imagery, so the selected
// collection's analyzed results come off the map for the duration of the draw —
// `useAreaDraw` snapshots what was visible beforehand and restores exactly that
// when the draw ends or is cancelled. 'hexagonLayer' is the model-path density
// overlay `useAreaDraw` hides by default; it's repeated here because passing
// `layersToHide` replaces that default rather than extending it.
const DRAW_LAYERS_TO_HIDE = ['hexagonLayer', ...COLLECTION_RESULT_LAYER_IDS];

const ObjectPickerModal = () => {
	const { colors } = useTheme();

	// antd re-processes the theme when this object's reference changes, and the
	// modal re-renders often (step/selection from the store). Memoize so the
	// theme reference is stable across renders and only rebuilds when a token changes.
	const modalTheme = useMemo(
		() => ({
			components: {
				// Keep inner cards a touch lighter than the modal surface so the
				// content stays readable and doesn't blend into the darker background.
				Card: {
					colorBgContainer: colors.backgroundAccentMuted,
				},
			},
		}),
		[colors.backgroundAccentMuted]
	);

	const isOpen = useObjectPickerStore(s => s.isOpen);
	const isDrawing = useObjectPickerStore(s => s.isDrawing);
	const closeAndReset = useObjectPickerStore(s => s.closeAndReset);

	const step = useObjectPickerStore(s => s.step);
	const setStep = useObjectPickerStore(s => s.setStep);
	const prevStep = useObjectPickerStore(s => s.prevStep);

	const areaMode = useObjectPickerStore(s => s.areaMode);
	const aoiPolygon = useObjectPickerStore(s => s.aoiPolygon);
	const setDrawing = useObjectPickerStore(s => s.setDrawing);
	const setAreaMode = useObjectPickerStore(s => s.setAreaMode);
	const setAoiPolygon = useObjectPickerStore(s => s.setAoiPolygon);

	// The Area step can advance only once a scope is chosen: the entire project,
	// or a drawn area that actually has a polygon.
	const canRunFromArea =
		areaMode === AREA_MODE.ENTIRE || (areaMode === AREA_MODE.DRAW && Boolean(aoiPolygon));

	// The area scope as it was when a draw started, so an abandoned redraw can be
	// put back exactly as it was instead of leaving the user with nothing.
	const previousAreaRef = useRef(null);

	// The draw lifecycle (map prep, freehand draw, teardown/restore) is shared
	// with the legacy Detect-in-area button via `useAreaDraw`. Here we hide the
	// modal while sketching and store the drawn ring as the area of interest.
	const { startDraw } = useAreaDraw({
		dialogue: DRAW_DIALOGUE,
		layersToHide: DRAW_LAYERS_TO_HIDE,
		onStart: () => {
			previousAreaRef.current = { areaMode, aoiPolygon };
			setAreaMode(AREA_MODE.DRAW);
			// Drop any existing area up front: a redraw replaces it, and leaving it
			// in the store keeps `AoiPreviewLayer` drawing the old polygon on the
			// map underneath the new sketch.
			setAoiPolygon(null);
			setDrawing(true);
		},
		onComplete: ring => {
			previousAreaRef.current = null;
			setAoiPolygon(ring);
			setDrawing(false);
		},
		onCancel: () => {
			const previous = previousAreaRef.current;
			previousAreaRef.current = null;
			if (previous) {
				setAreaMode(previous.areaMode);
				setAoiPolygon(previous.aoiPolygon);
			}
			setDrawing(false);
		},
	});

	const { previewDetection, confirmRun, isPreviewing, isConfirming, isRunning } =
		useRunDetection();

	const meta = STEP_META[step];

	const renderStep = () => {
		if (step === 0) return <Collections />;
		if (step === 1) return <AreaStep onDraw={startDraw} />;
		return <RunStep />;
	};

	const renderFooter = () => {
		// Choosing a collection advances on its own, so this step has no footer.
		if (step === 0) return null;

		// Continuing from the Area step asks the backend what a run would do. The
		// Run step is only reached when a run is actually needed, so an area that's
		// already been analyzed closes the wizard instead of asking to confirm
		// something that can't run.
		if (step === 1) {
			return (
				<Flex justify="space-between" align="center">
					<Button icon={<LuArrowLeft />} onClick={prevStep} disabled={isRunning}>
						Back
					</Button>
					<Button
						type="primary"
						icon={<LuArrowRight />}
						iconPlacement="end"
						disabled={!canRunFromArea}
						loading={isPreviewing}
						onClick={previewDetection}>
						Continue
					</Button>
				</Flex>
			);
		}

		return (
			<Flex justify="space-between" align="center">
				<Button icon={<LuArrowLeft />} onClick={prevStep} disabled={isRunning}>
					Back
				</Button>
				<Button
					type="primary"
					icon={<LuPlay />}
					loading={isConfirming}
					onClick={confirmRun}
					data-tour="picker-run">
					Yes, run detection
				</Button>
			</Flex>
		);
	};

	return (
		<ConfigProvider theme={modalTheme}>
			<Modal
				centered
				// Dismissal is blocked while a detect request is in flight. Letting
				// the wizard close mid-preview means the response lands on a wizard
				// that has already reset — and if the user reopens before it lands,
				// the fresh session inherits the discarded session's preview and
				// gets advanced a step. `enabled: false` keeps the map visible
				// behind the wizard; `closable` gates click-to-dismiss on the wrap,
				// which stays active even with the mask hidden.
				mask={{ enabled: false, closable: !isRunning }}
				closable={!isRunning}
				keyboard={!isRunning}
				footer={renderFooter()}
				title={
					<Flex vertical gap={12}>
						<Row align="middle" gutter={8} wrap={false}>
							<Col flex="none">
								<Button
									size="large"
									color="primary"
									variant="outlined"
									icon={<BiTargetLock size={20} style={{ marginTop: 5 }} />}
									style={{ borderRadius: 10 }}
								/>
							</Col>
							<Col flex="auto">
								{/* Beside the title, not in its own column: the Modal's close
								    button is absolutely positioned over the title row's end. */}
								<Flex align="center" gap={4}>
									<Typography.Title style={{ margin: 0 }} level={5}>
										Set up detection
									</Typography.Title>
									<ObjectPickerTour suppressed={isRunning} />
								</Flex>
								<Typography.Text type="secondary" style={{ fontSize: 12 }}>
									{meta.subtitle}
								</Typography.Text>
							</Col>
						</Row>
						<Divider size="small" />
						{/* Locked while a request is in flight, matching Back.
						    Without this the user can leave the Run step
						    mid-confirm and fire a second, concurrent detect. */}
						<Steps
							size="small"
							current={step}
							onChange={target => !isRunning && target < step && setStep(target)}
							items={PICKER_STEPS.map((_, index) => ({
								title: STEP_META[index].title,
								disabled: index > step || isRunning,
							}))}
						/>
					</Flex>
				}
				open={isOpen && !isDrawing}
				onCancel={closeAndReset}
				width={{
					xs: '90%',
					sm: '80%',
					md: '70%',
					lg: '60%',
					xl: '60%',
					xxl: '50%',
				}}>
				<Divider />
				{renderStep()}
			</Modal>
		</ConfigProvider>
	);
};

export default ObjectPickerModal;
